import React, { useRef, useState } from "react";
import { FiImage } from "react-icons/fi";
import { compressImageFile } from "../../../shared/utils/compressImage";
import { useOnboarding } from "../OnboardingContext";
import OnboardingWizard from "../OnboardingWizard";
import { useEditStepSave } from "../useEditStepSave";

const PHOTO_SLOT_COUNT = 6;

export default function StepPhotos() {
  const { draft, updateDraft, goToNextStep, goToPrevStep, isEditMode, progressSaveError } = useOnboarding();
  const fileRefs = useRef([]);
  const [photoError, setPhotoError] = useState("");

  const slots = draft.photoSlots ?? Array(PHOTO_SLOT_COUNT).fill(null);

  const setPhotoAt = (index, value) => {
    const nextSlots = [...slots];
    nextSlots[index] = value;
    updateDraft({
      photoSlots: nextSlots,
      photos: nextSlots.filter(Boolean),
    });
  };

  const handleFile = async (index, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file?.type?.startsWith("image/")) return;

    setPhotoError("");

    try {
      const dataUrl = await compressImageFile(file);
      setPhotoAt(index, dataUrl);
    } catch {
      setPhotoError("Could not process that image. Try a different photo or a smaller file.");
    }
  };

  const hasPrimary = Boolean(slots[0]);
  const { showSave, onSave, saveDisabled, saveLabel, saveError } = useEditStepSave({
    saveDisabled: !hasPrimary,
  });

  return (
    <OnboardingWizard
      hideChrome
      stepKey="photos"
      title={isEditMode ? "Your photos" : "Upload your photos"}
      subtitle={isEditMode
        ? "Update your gallery. Your first photo is still your main profile picture."
        : "Your first photo is your main profile picture. Add a few more to stand out."}
      onBack={() => goToPrevStep("photos")}
      onContinue={() => goToNextStep("photos")}
      continueDisabled={!hasPrimary}
      continueLabel="Continue"
      showSave={showSave}
      onSave={onSave}
      saveDisabled={saveDisabled}
      saveLabel={saveLabel}
    >
      {saveError || photoError || progressSaveError ? (
        <p className="onboarding-error-banner" role="alert">
          {saveError || photoError || progressSaveError}
        </p>
      ) : null}
      <div
        className="onboarding-photo-grid"
        style={{
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gridTemplateAreas: '"main main top" "main main mid" "bottom1 bottom2 bottom3"',
        }}
      >
        {slots.map((photo, index) => {
          const isMain = index === 0;
          const gridArea = ["main", "top", "mid", "bottom1", "bottom2", "bottom3"][index];
          return (
            <div
              key={index}
              className={`onboarding-photo-slot${isMain ? " onboarding-photo-slot--main" : ""}`}
              style={{ gridArea }}
            >
              <input
                ref={(el) => { fileRefs.current[index] = el; }}
                type="file"
                accept="image/*"
                className="onboarding-photo-slot__file"
                onChange={(e) => handleFile(index, e)}
              />
              {photo ? (
                <>
                  <img src={photo} alt="" className="onboarding-photo-slot__image" />
                  <button
                    type="button"
                    className="onboarding-photo-slot__remove"
                    onClick={() => setPhotoAt(index, null)}
                    aria-label="Remove photo"
                  >
                    ×
                  </button>
                  {isMain ? (
                    <div className="onboarding-photo-slot__actions">
                      <button
                        type="button"
                        className="onboarding-photo-slot__btn"
                        onClick={() => fileRefs.current[index]?.click()}
                      >
                        Change photo
                      </button>
                    </div>
                  ) : null}
                </>
              ) : (
                <button
                  type="button"
                  className={`onboarding-photo-slot__empty${
                    isMain ? " onboarding-photo-slot__empty--main" : ""
                  }`}
                  onClick={() => fileRefs.current[index]?.click()}
                  aria-label={isMain ? "Upload main photo" : "Add photo"}
                >
                  {isMain ? (
                    <FiImage className="onboarding-photo-slot__icon" aria-hidden />
                  ) : (
                    <span>+ Add</span>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </OnboardingWizard>
  );
}
